import React, { useState, useEffect } from 'react';
import {
  DollarSign, Users, GraduationCap, BarChart2, BookOpen,
  TrendingUp, AlertCircle, Loader2, CheckCircle, Clock,
  ShieldAlert, UserCheck,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend, AreaChart, Area,
} from 'recharts';
import { toast } from 'sonner';
import api from '../../api';

const COLORS = ['#2563eb', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed'];

const KPI = ({ icon: Icon, label, value, sub, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
    teal: 'bg-teal-50 text-teal-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-sm font-medium text-slate-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
};

const GradeChip = ({ grade }) => {
  const c = {
    A: 'bg-emerald-100 text-emerald-700',
    B: 'bg-blue-100 text-blue-700',
    C: 'bg-yellow-100 text-yellow-700',
    F: 'bg-red-100 text-red-700',
  }[grade] || 'bg-slate-100 text-slate-600';
  return <span className={`text-xs font-bold px-2 py-0.5 rounded ${c}`}>{grade}</span>;
};

export default function BusinessViewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/analytics/business-view')
      .then(r => { if (r.success) setData(r.data); })
      .catch(() => toast.error('Failed to load Business View'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="business-view-page">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center text-slate-500" data-testid="business-view-page">
        <AlertCircle className="w-10 h-10 mx-auto mb-2 text-slate-300" />
        <p>Failed to load data. Please refresh.</p>
      </div>
    );
  }

  const fees = data.fees || {};
  const enrollment = data.enrollment || {};
  const collectionRate = fees.collection_rate || 0;

  // Pie data for fees
  const feePieData = [
    { name: 'Collected', value: fees.total_paid || 0 },
    { name: 'Outstanding', value: fees.total_balance || 0 },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="business-view-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Business View
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Proprietor overview — money, enrollment, staff, academic quality, and operations
        </p>
      </div>

      {/* Top KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI
          icon={Users} label="Total Students" color="blue"
          value={enrollment.total_students?.toLocaleString() || '—'}
          sub={`${enrollment.total_teachers || 0} teachers`}
          data-testid="bv-kpi-students"
        />
        <KPI
          icon={DollarSign} label="Total Invoiced" color="green"
          value={fees.total_invoiced ? `₦${(fees.total_invoiced / 1000).toFixed(0)}K` : '—'}
          sub={`₦${((fees.total_paid || 0) / 1000).toFixed(0)}K collected`}
        />
        <KPI
          icon={TrendingUp} label="Collection Rate" color={collectionRate >= 70 ? 'green' : collectionRate >= 50 ? 'yellow' : 'red'}
          value={`${collectionRate}%`}
          sub={`${fees.paid_count || 0} fully paid invoices`}
        />
        <KPI
          icon={AlertCircle} label="Outstanding Balance" color="yellow"
          value={fees.total_balance ? `₦${(fees.total_balance / 1000).toFixed(0)}K` : '₦0'}
          sub={`${fees.unpaid_count || 0} unpaid invoices`}
        />
      </div>

      {/* Enrollment by class + Fee breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* Enrollment by class */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="enrollment-chart">
          <h3 className="font-bold text-slate-800 mb-4">Enrollment by Class</h3>
          {(enrollment.by_class || []).length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10">No class data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={enrollment.by_class || []} margin={{ top: 0, right: 8, bottom: 20, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="class_name" tick={{ fontSize: 11, fill: '#64748b' }} angle={-35} textAnchor="end" />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" name="Students" radius={[4, 4, 0, 0]}>
                  {(enrollment.by_class || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Fee collection pie */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-4">Fee Collection Breakdown</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={feePieData}
                cx="50%" cy="50%"
                innerRadius={60} outerRadius={90}
                paddingAngle={3}
                dataKey="value"
              >
                <Cell fill="#2563eb" />
                <Cell fill="#fca5a5" />
              </Pie>
              <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              <Tooltip formatter={v => `₦${v.toLocaleString()}`} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-around text-center mt-2">
            <div>
              <p className="text-lg font-bold text-blue-600">₦{(fees.total_paid || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-500">Collected</p>
            </div>
            <div>
              <p className="text-lg font-bold text-red-400">₦{(fees.total_balance || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-500">Outstanding</p>
            </div>
          </div>
        </div>
      </div>

      {/* Trend Charts */}
      {(data.trends?.monthly_fees || data.trends?.monthly_enrollment) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          {/* Fee collection trend */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="fee-trend-chart">
            <h3 className="font-bold text-slate-800 mb-1">Monthly Fee Collections</h3>
            <p className="text-xs text-slate-400 mb-4">Last 6 months</p>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.trends.monthly_fees || []} margin={{ top: 0, right: 8, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="feeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={v => v === 0 ? '0' : `₦${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={v => `₦${v.toLocaleString()}`} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="amount" name="Collections" stroke="#2563eb" strokeWidth={2} fill="url(#feeGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Enrollment trend */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="enrollment-trend-chart">
            <h3 className="font-bold text-slate-800 mb-1">Monthly New Enrolments</h3>
            <p className="text-xs text-slate-400 mb-4">Last 6 months</p>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.trends.monthly_enrollment || []} margin={{ top: 0, right: 8, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="enrollGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="count" name="New Students" stroke="#059669" strokeWidth={2} fill="url(#enrollGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Academic quality */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm mb-5" data-testid="academic-quality-section">
        <h3 className="font-bold text-slate-800 mb-4">Academic Quality by Class</h3>
        {(data.academic_quality || []).length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No approved results yet. Approve exam results to see class performance.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 font-semibold text-slate-500 text-xs uppercase">Class</th>
                  <th className="text-center py-2 font-semibold text-slate-500 text-xs uppercase">Avg Score</th>
                  <th className="text-center py-2 font-semibold text-slate-500 text-xs uppercase">Students</th>
                  <th className="text-center py-2 font-semibold text-slate-500 text-xs uppercase">Grade</th>
                </tr>
              </thead>
              <tbody>
                {(data.academic_quality || []).map(cls => (
                  <tr key={cls.class_id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2.5 font-medium text-slate-800">{cls.class_name}</td>
                    <td className="py-2.5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="flex-1 max-w-[100px] h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-blue-500"
                            style={{ width: `${cls.avg_score}%` }}
                          />
                        </div>
                        <span className="font-semibold text-slate-700">{cls.avg_score}%</span>
                      </div>
                    </td>
                    <td className="py-2.5 text-center text-slate-500">{cls.student_count}</td>
                    <td className="py-2.5 text-center"><GradeChip grade={cls.grade} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Teacher workload + Operations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Teacher workload */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="teacher-workload-section">
          <h3 className="font-bold text-slate-800 mb-4">Teacher Workload</h3>
          {(data.teacher_workload || []).length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No teacher assignments found.</p>
          ) : (
            <div className="space-y-3">
              {(data.teacher_workload || []).slice(0, 8).map(t => (
                <div key={t.teacher_id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600 flex-shrink-0">
                    {(t.teacher_name || '?')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800 truncate">{t.teacher_name}</span>
                      {t.overloaded && (
                        <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium ml-2">Overloaded</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">{t.assignment_count} assignment{t.assignment_count !== 1 ? 's' : ''} · {t.class_count} class{t.class_count !== 1 ? 'es' : ''}</p>
                  </div>
                  <div className="text-sm font-bold text-slate-700 w-6 text-right">{t.assignment_count}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Operations */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="operations-section">
          <h3 className="font-bold text-slate-800 mb-4">Operations Snapshot</h3>
          <div className="space-y-4">
            {/* Lesson notes */}
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <BookOpen className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex justify-between items-center">
                  <p className="text-sm font-semibold text-slate-700">Lesson Notes</p>
                  <span className="text-sm font-bold text-slate-800">
                    {data.operations?.lesson_notes?.compliance_rate || 0}% compliance
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {data.operations?.lesson_notes?.approved || 0} approved ·{' '}
                  {data.operations?.lesson_notes?.pending || 0} pending review
                </p>
              </div>
            </div>

            {/* Admissions */}
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <UserCheck className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex justify-between items-center">
                  <p className="text-sm font-semibold text-slate-700">Admissions</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    (data.operations?.admissions?.pending || 0) > 0
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {data.operations?.admissions?.pending || 0} pending
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {data.operations?.admissions?.total || 0} total applications
                </p>
              </div>
            </div>

            {/* Behavior incidents */}
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <ShieldAlert className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex justify-between items-center">
                  <p className="text-sm font-semibold text-slate-700">Behavior Incidents</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    (data.operations?.behavior_incidents?.open || 0) > 0
                      ? 'bg-red-100 text-red-700'
                      : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {data.operations?.behavior_incidents?.open || 0} open
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {data.operations?.behavior_incidents?.total || 0} total incidents recorded
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Payments */}
      {(fees.recent_payments || []).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="recent-payments-section">
          <h3 className="font-bold text-slate-800 mb-4">Recent Payments</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 font-semibold text-slate-500 text-xs uppercase">Student</th>
                  <th className="text-center py-2 font-semibold text-slate-500 text-xs uppercase">Amount</th>
                  <th className="text-center py-2 font-semibold text-slate-500 text-xs uppercase">Method</th>
                  <th className="text-right py-2 font-semibold text-slate-500 text-xs uppercase">Date</th>
                </tr>
              </thead>
              <tbody>
                {fees.recent_payments.map(p => (
                  <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2.5 font-medium text-slate-800">{p.student_name}</td>
                    <td className="py-2.5 text-center text-emerald-600 font-semibold">₦{(p.amount || 0).toLocaleString()}</td>
                    <td className="py-2.5 text-center">
                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded capitalize">{p.payment_method || 'cash'}</span>
                    </td>
                    <td className="py-2.5 text-right text-slate-400 text-xs">{(p.created_at || '').slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
